import { useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQProps {
  location?: string;
}

const FAQ = ({ location = "Villeurbanne" }: FAQProps) => {
  const faqData = useMemo(() => [
    {
      question: `Combien coûte une ouverture de porte sans effraction à ${location} ?`,
      answer: "Le tarif pour une ouverture de porte sans effraction varie selon l'heure et le type de serrure. Nous vous communiquons un devis précis et transparent par téléphone avant notre intervention. Aucun frais caché, le prix annoncé est le prix final."
    },
    {
      question: location.includes("Lyon") ? `Quel est votre délai d'intervention dans le ${location} ?` : `Quel est votre délai d'intervention sur ${location} ?`,
      answer: `Nous intervenons en urgence 24h/24 et 7j/7. Notre délai moyen d'intervention est de 20 à 30 minutes sur ${location} et les communes limitrophes. Pour les zones plus éloignées du Grand Lyon, comptez 45 minutes maximum selon la circulation.`
    },
  {
    question: "Acceptez-vous les paiements par carte bancaire ?",
    answer: "Oui, nous acceptons tous les moyens de paiement : espèces, carte bancaire (avec terminal mobile), chèque, et virement bancaire. Une facture détaillée vous est systématiquement remise à la fin de notre intervention pour faciliter vos démarches avec votre assurance si nécessaire."
  },
  {
    question: "Travaillez-vous avec les assurances ?",
    answer: "Absolument ! Nous fournissons une facture détaillée conforme aux exigences des compagnies d'assurance habitation. En cas de cambriolage ou d'effraction, notre facture vous permettra d'obtenir le remboursement auprès de votre assureur. Nous pouvons également vous conseiller sur les démarches à suivre."
  },
  {
    question: "Quels sont vos tarifs de nuit et week-end ?",
    answer: "Nos tarifs appliquent une majoration pour les interventions de nuit (18h30-8h), dimanches et jours fériés, conformément aux usages de la profession. Le tarif exact vous est communiqué lors de votre appel, avant notre déplacement. Nous restons l'un des serruriers les plus transparents et compétitifs de Villeurbanne."
  },
  {
    question: location.includes("Lyon") ? `Intervenez-vous dans tout le ${location} ?` : `Couvrez-vous tout le secteur de ${location} ?`,
    answer: `Oui, nous intervenons sur ${location} et dans tous les secteurs environnants. Nous couvrons également l'ensemble de Villeurbanne, Lyon (tous arrondissements) et les communes de la métropole comme Bron, Vaulx-en-Velin, Caluire, Décines, Meyzieu, etc.`
  },
  {
    question: "Pouvez-vous ouvrir ma porte sans la casser ?",
    answer: "Dans la majorité des cas, oui ! Nos serruriers sont formés aux techniques d'ouverture fine qui préservent votre porte et votre serrure. Pour une simple porte claquée, nous utilisons des outils spécialisés qui n'endommagent rien. En cas de serrure plus complexe ou de cylindre cassé, nous vous informons avant toute intervention destructive."
  },
  {
    question: "Proposez-vous un devis gratuit ?",
    answer: "Oui, nous établissons systématiquement un devis gratuit et sans engagement par téléphone avant notre intervention. Sur place, si la situation nécessite des travaux supplémentaires (remplacement de serrure, blindage, etc.), nous vous présentons un nouveau devis détaillé que vous êtes libre d'accepter ou de refuser."
  }
  ], [location]);

  useEffect(() => {
    // Add FAQ Schema.org structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [faqData]);

  return (
    <section className="py-16 px-4 bg-background">
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
          Questions fréquentes
        </h2>
        <p className="text-center text-muted-foreground mb-12 text-lg">
          Tout ce que vous devez savoir sur nos services de serrurerie {location && `à ${location}`}
        </p>

        <Accordion type="single" collapsible className="w-full space-y-4">
          {faqData.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="border border-border rounded-lg px-6 bg-card"
            >
              <AccordionTrigger className="text-left text-base md:text-lg font-semibold hover:text-primary">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Vous avez d'autres questions ? N'hésitez pas à nous contacter !
          </p>
          <a 
            href="tel:0482531608" 
            className="inline-flex items-center justify-center px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors"
          >
            Appelez-nous au 04 82 53 16 08
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
